import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import BasePageWithHeader from '../../components/BasePageWithHeader';
import {
    IonContent,
    IonCard,
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonList,
    useIonRouter,
    IonToast,
    IonAvatar,
    IonText,
    IonIcon,
    useIonToast,
    IonReorderGroup,
    IonReorder,
    ItemReorderEventDetail,
    IonGrid,
    IonRow,
    IonCol,
} from '@ionic/react';
import { ContactFormData, CustomField, Contact } from '@rsmaids/shared/types/contacts';
import { processPhoneWithLibrary, isValidPhone } from '@rsmaids/shared/services/api/contacts'; // Updated import
import { getCachedContact } from '../../utils/contactCache';
import { pinContactSaved } from '../../hooks/useContactLiveSync';
import { contactSavedToast } from '../../utils/contactToasts';
import styles from './NewContact.module.scss';  // Update to correct SCSS file
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import InlineTypePicker from '../../components/InlineTypePicker';
import PhoneAgentInput, { type PhoneTypeOption } from '../../components/PhoneAgentInput';
import MiniCalendarPicker from '../../components/MiniCalendarPicker';
import LocationPicker from '../../components/LocationPicker';
import { trash, trashOutline, add, chevronBack, closeOutline, mailOutline, callOutline, homeOutline, pinOutline, businessOutline, briefcaseOutline, calendarOutline, informationCircle, chevronDownOutline, alertCircleOutline } from 'ionicons/icons';
import { CustomFieldsManager } from '../../components/CustomFieldsManager';
import SheetModal from '../../components/SheetModal';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { intlLocale } from '../../i18n/locale';


// Utility function to generate unique IDs
const generateUniqueId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const validatePhoneNumber = (phone: string): boolean => {
    return isValidPhone(phone); // That's it! Much simpler
};

const PHONE_TYPE_OPTIONS: PhoneTypeOption[] = [
    { value: 'mobile', get label() { return i18n.t('pages.bookingSystem.NewContact.mobile'); }, icon: callOutline },
    { value: 'work', get label() { return i18n.t('pages.bookingSystem.NewContact.work'); }, icon: briefcaseOutline },
    { value: 'home', get label() { return i18n.t('pages.bookingSystem.NewContact.home'); }, icon: homeOutline },
    { value: 'fax', get label() { return i18n.t('pages.bookingSystem.NewContact.fax'); }, icon: callOutline },
];

type PickerRect = { top: number; left?: number; right?: number; maxHeight: number; minWidth?: number };

function usePickerDropdown(
    isOpen: boolean,
    fieldRef: React.RefObject<HTMLElement | null>,
    portalRef: React.RefObject<HTMLElement | null>,
    cardRef: React.RefObject<HTMLElement | null>,
    close: () => void,
    align: 'left' | 'right',
    matchWidth = false,
) {
    const [rect, setRect] = useState<PickerRect | null>(null);
    // Clear the computed rect the moment the dropdown closes — computed
    // during render rather than as a synchronous setState call at the top
    // of the effect below.
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (!isOpen && rect !== null) setRect(null);
    }

    useEffect(() => {
        if (!isOpen) return;

        const computeRect = (): PickerRect | null => {
            const field = fieldRef.current;
            const portal = portalRef.current;
            if (!field || !portal) return null;
            const box = field.getBoundingClientRect();
            const anchor = portal.getBoundingClientRect();
            const maxHeight = Math.max(120, Math.min(320, window.innerHeight - box.bottom - 12));
            const minWidth = matchWidth ? box.width : undefined;
            return align === 'left'
                ? { top: box.bottom - anchor.top + 4, left: box.left - anchor.left, maxHeight, minWidth }
                : { top: box.bottom - anchor.top + 4, right: anchor.right - box.right, maxHeight, minWidth };
        };

        const applyRect = (r: PickerRect) => {
            const card = cardRef.current;
            if (!card) return;
            card.style.top = `${r.top}px`;
            card.style.left = r.left !== undefined ? `${r.left}px` : '';
            card.style.right = r.right !== undefined ? `${r.right}px` : '';
            card.style.maxHeight = `${r.maxHeight}px`;
            card.style.minWidth = r.minWidth !== undefined ? `${r.minWidth}px` : '';
        };

        const initial = computeRect();
        setRect(initial);

        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                const r = computeRect();
                if (r) {
                    setRect(r);
                    applyRect(r);
                }
            });
        });

        const handleScroll = () => {
            const r = computeRect();
            if (r) applyRect(r);
        };
        const handleResize = () => {
            const r = computeRect();
            if (r) {
                setRect(r);
                applyRect(r);
            }
        };
        const handleOutsidePointer = (e: PointerEvent) => {
            const target = e.target as Node;
            if (fieldRef.current?.contains(target)) return;
            if (portalRef.current?.contains(target)) return;
            close();
        };

        document.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        document.addEventListener('pointerdown', handleOutsidePointer);

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('pointerdown', handleOutsidePointer);
        };
    }, [isOpen, fieldRef, portalRef, cardRef, close, align, matchWidth]);

    return rect;
}

function PickerDropdown({
    isOpen,
    fieldRef,
    portalRef,
    close,
    align,
    className,
    children,
    matchWidth,
}: {
    isOpen: boolean;
    fieldRef: React.RefObject<HTMLElement | null>;
    portalRef: React.RefObject<HTMLElement | null>;
    close: () => void;
    align: 'left' | 'right';
    className: string;
    matchWidth?: boolean;
    children: React.ReactNode;
}) {
    const cardRef = useRef<HTMLIonCardElement>(null);
    const rect = usePickerDropdown(isOpen, fieldRef, portalRef, cardRef, close, align, matchWidth);
    const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setPortalNode(portalRef.current);
    }, [portalRef, isOpen]);
    if (!isOpen || !rect || !portalNode) return null;

    return createPortal(
        <IonCard ref={cardRef} className={className} style={{ ...rect, position: 'absolute' }}>
            {children}
        </IonCard>,
        portalNode,
    );
}

const NewContact: React.FC<{ contactId?: string; onClose?: () => void }> = ({ contactId, onClose }) => {
  const { t } = useTranslation();
    const ionRouter = useIonRouter();
    const { id: routeId } = useParams<{ id: string }>();
    // Edit mode is driven by a route `:id` when NewContact is a page, or by the
    // `contactId` prop when it's rendered as an edit modal (Contact.tsx's
    // "Edit Contact" menu item).
    const id = routeId || contactId;
    const [isEditMode, setIsEditMode] = useState(!!id);
    const [showToast, setShowToast] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<ContactFormData>({
        id: '',
        firstName: '',
        lastName: '',
        emails: [],
        phones: [],
        addresses: [],
        customFields: [],
        address: '', // Add default value for address
        addressLine1: '' // Add default value for addressLine1
    });
    // Initialize with default avatar URL
    const [avatar, setAvatar] = useState<string>('https://ui-avatars.com/api/?name=+&background=4ECDC4&color=fff');

    // Add state to track member status (around line 45):
    const [contactMemberInfo, setContactMemberInfo] = useState<{
        isMember: boolean;
        memberStatus: string | null;
    }>({
        isMember: false,
        memberStatus: null
    });

    const [emailType, setEmailType] = useState('personal');
    const [phoneType, setPhoneType] = useState('home');
    const [addressType, setAddressType] = useState('home');
    const [present] = useIonToast();

    const STATE_TRIGGER_ID = 'state-select-trigger';
    const COUNTRY_TRIGGER_ID = 'country-select-trigger';

    const DISPLAY_NAME_SOURCE_OPTIONS: { value: 'company' | 'name'; label: string }[] = [
        { value: 'company', label: t('pages.bookingSystem.NewContact.companyName') },
        { value: 'name', label: t('pages.bookingSystem.NewContact.fullName') },
    ];
    const [isDisplayNameSourceOpen, setIsDisplayNameSourceOpen] = useState(false);
    const displayNameSourceFieldRef = useRef<HTMLIonButtonElement>(null);
    const pickerPortalRef = useRef<HTMLDivElement>(null);
    const [isBirthdateOpen, setIsBirthdateOpen] = useState(false);
    const birthdateFieldRef = useRef<HTMLIonButtonElement>(null);

    // "Add New Field" — one consolidated SheetModal (matching Filter Contacts / desktop's
    // own Add New Field menu) listing Email/Phone/Address (redundant with their own
    // separate +Add buttons elsewhere on this form) plus Company/Position/Birthdate
    // (optional standard Wix fields, no dedicated UI on mobile until now) plus Custom
    // Field, which hands off to the existing CustomFieldsManager modal.
    const [showAddFieldSheet, setShowAddFieldSheet] = useState(false);
    const [addedOptionalFields, setAddedOptionalFields] = useState<('company' | 'position' | 'birthdate')[]>([]);
    const [showCustomFieldsManager, setShowCustomFieldsManager] = useState(false);



    // Add near other state declarations
    const [modalCustomFields, setModalCustomFields] = useState<CustomField[]>([]);
    const contactService = DataServiceFactory.getService();

    const [errorMessage, setErrorMessage] = useState('');

    // Add this near other state declarations
    const [validationErrors, setValidationErrors] = useState<{
        firstName: boolean;
        lastName: boolean;
        phone: boolean;
        email: boolean;
        addressLine1: boolean;
        city: boolean;
        state: boolean;
        zipCode: boolean;
        country: boolean
    }>({
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
        addressLine1: false,
        city: false,
        state: false,
        zipCode: false,
        country: false
    });


    const addField = (fieldType: 'emails' | 'phones' | 'addresses') => {
        setFormData(prev => ({
            ...prev,
            [fieldType]: [...prev[fieldType], {
                id: generateUniqueId(),
                ...(fieldType === 'phones' && { phone: '', formattedPhone: '', e164Phone: '', countryCode: '' }),
                ...(fieldType === 'emails' && { email: '' }),
                ...(fieldType === 'addresses' && { address: { addressLine1: '', addressLine2: '', city: '', subdivision: '', postalCode: '', country: '' } }),
                tag: '',
                primary: false
            }]
        }));
    };

    const removeField = (fieldType: 'emails' | 'phones' | 'addresses', id: string) => {
        setFormData(prev => {
            const filteredFields = prev[fieldType].filter(field => field.id !== id);
            return {
                ...prev,
                [fieldType]: filteredFields
            };
        });
    };

    useEffect(() => {
        const loadExistingCustomFields = async () => {
            try {
                // Get all existing custom fields from the service
                const allCustomFields = await contactService.contacts.getAllCustomFields();

                if (allCustomFields && allCustomFields.length > 0) {
                    // ✅ Map fields to include their actual Wix keys
                    const fieldsWithKeys = allCustomFields.map(field => ({
                        ...field,
                        fieldKey: `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}` // Store the actual key
                    }));
                    setModalCustomFields(fieldsWithKeys);
                }
            } catch (error) {
                console.error('Error loading existing custom fields:', error);
            }
        };

        loadExistingCustomFields();
    }, [contactService]);

    // Only auto-generate a name-based placeholder in create mode. In edit mode this
    // used to fire on every name change too — including the very first one, the
    // instant populateFormFromContact sets firstName/lastName from the loaded
    // contact — which was overwriting the contact's real uploaded avatar (just set
    // by that same populate call) with a generated initials avatar. Computed
    // during render (React's "adjust state when a dependency changes"
    // pattern) rather than as setState calls inside an effect, since it's a
    // pure derivation from already-available props/state.
    const [prevAvatarInputs, setPrevAvatarInputs] = useState({ firstName: formData.firstName, lastName: formData.lastName, isEditMode });
    if (
        prevAvatarInputs.firstName !== formData.firstName ||
        prevAvatarInputs.lastName !== formData.lastName ||
        prevAvatarInputs.isEditMode !== isEditMode
    ) {
        setPrevAvatarInputs({ firstName: formData.firstName, lastName: formData.lastName, isEditMode });
        if (!isEditMode) {
            // Only update avatar if name has content
            if (formData.firstName?.trim() || formData.lastName?.trim()) {
                setAvatar(getDefaultAvatar(`${formData.firstName} ${formData.lastName}`));
            } else {
                // Reset to default avatar if name is empty
                setAvatar('https://ui-avatars.com/api/?name=+&background=4ECDC4&color=fff');
            }
        }
    }

    useEffect(() => {
        // Get all CustomSelector buttons
        const selectorButtons = document.querySelectorAll('.customSelectorButton');

        // Add click event listeners to prevent scrolling
        const handleSelectorClick = (e: Event) => {
            e.preventDefault();
            const scrollPosition = window.scrollY;

            // Force scroll position to remain the same
            setTimeout(() => {
                window.scrollTo(0, scrollPosition);
            }, 0);
        };

        // Add event listeners
        selectorButtons.forEach(button => {
            button.addEventListener('click', handleSelectorClick);
        });

        // Clean up
        return () => {
            selectorButtons.forEach(button => {
                button.removeEventListener('click', handleSelectorClick);
            });
        };
    }, []);

    // Initialize the service

    // Detect edit mode — computed during render (React's "adjust state
    // when a dependency changes" pattern) rather than as a setState call
    // inside an effect, since it's a pure derivation from the id prop.
    if (id && !isEditMode) {
        setIsEditMode(true);
    }

    // Load contact data for editing. Calls loadContactForEditing via a ref
    // (kept current below) rather than listing it as a dependency directly —
    // it and populateFormFromContact are large, interdependent functions
    // redefined every render, and this effect must only fire on an actual id
    // change, not on every render of the component.
    const loadContactForEditingRef = useRef<(contactId: string) => void>(() => {});
    useEffect(() => {
        if (!id) return;
        // Deferred via a resolved-promise microtask (not called
        // synchronously in the effect body).
        Promise.resolve().then(() => loadContactForEditingRef.current(id));
    }, [id]);

    // Update the loadContactForEditing function to also load all available custom fields:

    // Guards populateFormFromContact (which wholesale-replaces formData) against firing
    // more than once for the same mount — React 18 dev-mode StrictMode intentionally
    // double-invokes effects, and without this a second, late-arriving load could reset
    // formData back to the server's original data, silently dropping anything the user
    // had already typed (e.g. a newly-added additional email) in between.
    const hasPopulatedFormRef = React.useRef(false);
    const originalContactRef = useRef<Contact | null>(null);

    // Helper function to populate form from contact data
    const populateFormFromContact = async (contactData: Contact) => {
        // Set member info
        setContactMemberInfo({
            isMember: contactData.isMember || false,
            memberStatus: contactData.memberStatus || null
        });

        // Set avatar — real uploaded photo if there is one, else a name-based
        // placeholder (the auto-generate effect is skipped in edit mode, so this is
        // the only place that sets a fallback for a contact with no photo).
        if (contactData.avatar) {
            setAvatar(contactData.avatar);
        } else {
            const fullName = `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim();
            setAvatar(fullName ? getDefaultAvatar(fullName) : 'https://ui-avatars.com/api/?name=+&background=4ECDC4&color=fff');
        }

        // Apply global field ordering to custom fields
        let orderedCustomFields = contactData.customFields || [];
        if (orderedCustomFields.length > 0) {
            try {
                const globalOrder = await contactService.getGlobalFieldOrder();
                if (globalOrder.length > 0) {
                    orderedCustomFields = orderedCustomFields.sort((a: CustomField, b: CustomField) => {
                        const aIndex = globalOrder.indexOf(a.fieldKey || a.label);
                        const bIndex = globalOrder.indexOf(b.fieldKey || b.label);
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                    });
                }
            } catch {
                // Error loading global field order - continue with unordered fields
            }
        }

        // Every phone/email/address row is looked up elsewhere in this form via
        // `formData.X.find(item => item.id === someField.id)` (and .findIndex(...) for
        // edits) — if a source (e.g. a stale cache entry from before ids were added, or
        // any other path that doesn't stamp one) provides an entry with no id, EVERY
        // entry missing an id compares equal (undefined === undefined), so that lookup
        // silently matches the *first* such entry instead of the intended one — which
        // corrupts whichever entry gets edited, not just the display. Backfill here so
        // nothing downstream can ever see an id-less entry.
        const phonesWithIds = (contactData.phones || []).map(p => ({ ...p, id: p.id || generateUniqueId() }));
        const emailsWithIds = (contactData.emails || []).map(e => ({ ...e, id: e.id || generateUniqueId() }));
        const addressesWithIds = (contactData.addresses || []).map(a => ({ ...a, id: a.id || generateUniqueId() }));

        // Set form data with ordered fields
        setFormData(prevData => ({
            ...prevData,
            id: contactData.id,
            firstName: contactData.firstName || '',
            lastName: contactData.lastName || '',
            displayNameSource: contactData.displayNameSource,
            company: contactData.company || '',
            jobTitle: contactData.jobTitle || '',
            birthdate: contactData.birthdate || '',
            emails: emailsWithIds,
            phones: phonesWithIds,
            addresses: addressesWithIds,
            customFields: orderedCustomFields
        }));

        // Pre-mark optional fields that already have a value so their inputs show.
        const preAdded: ('company' | 'position' | 'birthdate')[] = [];
        if (contactData.company) preAdded.push('company');
        if (contactData.jobTitle) preAdded.push('position');
        if (contactData.birthdate) preAdded.push('birthdate');
        setAddedOptionalFields(preAdded);

        // Set type defaults
        setEmailType(contactData.emails?.[0]?.tag || 'personal');
        setPhoneType(contactData.phones?.[0]?.tag || 'mobile');
        setAddressType(contactData.addresses?.[0]?.tag || 'home');
    };

    const loadContactForEditing = async (contactId: string) => {
        try {
            const result = await getCachedContact(contactId, () => contactService.contacts.getContactById(contactId));

            // Load all available custom fields for the modal
            const allCustomFields = await contactService.contacts.getAllCustomFields();

            if (allCustomFields && allCustomFields.length > 0) {
                setModalCustomFields(allCustomFields);
            }

            if (result.data && !hasPopulatedFormRef.current) {
                hasPopulatedFormRef.current = true;
                // What the save toast and the contact page compare against. A deep copy: the form's inputs edit
                // the nested address/phone/email objects in place, which would otherwise change this "before" too.
                originalContactRef.current = JSON.parse(JSON.stringify(result.data)) as Contact;
                populateFormFromContact(result.data);
            }
        } catch (error) {
            console.error('Error loading contact for editing:', error);
            setErrorMessage(t('pages.bookingSystem.NewContact.failedToLoadContactDataFor'));
            setShowToast(true);
        }
    };
    useEffect(() => {
        loadContactForEditingRef.current = loadContactForEditing;
    });

    // Around line 174, add this refresh handler:

    const handleRefreshModalFields = async () => {
        try {
            const refreshedFields = await contactService.contacts.getAllCustomFields();
            setModalCustomFields(refreshedFields);

            // Update form custom fields to match renamed fields
            if (formData.customFields && formData.customFields.length > 0) {
                const updatedFormFields = formData.customFields
                    .filter(formField => {
                        // Remove deleted fields (fields that no longer exist in refreshedFields)
                        const stillExists = refreshedFields.some(rf => rf.fieldKey === formField.fieldKey);
                        return stillExists;
                    })
                    .map(formField => {
                        // Find the corresponding refreshed field by fieldKey
                        const refreshedField = refreshedFields.find(rf => rf.fieldKey === formField.fieldKey);
                        if (refreshedField && refreshedField.label !== formField.label) {
                            return {
                                ...formField,
                                label: refreshedField.label // ✅ Update to the new display name
                            };
                        }
                        return formField;
                    });

                setFormData(prev => ({
                    ...prev,
                    customFields: updatedFormFields
                }));
            }
        } catch {
            // Error refreshing modal fields
        }
    };

    // Required fields for enabling Save: name always; phone + email always
    // (both marked *). Address fields are only enforced once any address text
    // has been entered (the section is effectively optional until then).
    const addr = formData.addresses[0]?.address;
    const addressStarted = !!(addr && (addr.addressLine1 || addr.city || addr.postalCode || addr.subdivision || addr.country));
    const isFormValid =
        !!formData.firstName?.trim() &&
        !!formData.lastName?.trim() &&
        !!formData.phones[0]?.phone?.trim() &&
        !!formData.emails[0]?.email?.trim() &&
        (!addressStarted || !!(addr!.addressLine1 && addr!.city && addr!.postalCode && addr!.subdivision && addr!.country));

    // Modify your existing handleSubmit function to handle both create and update
    const handleSubmit = async () => {
        // Prevent double submissions
        if (isSubmitting) return;
        setIsSubmitting(true);

        for (let i = 0; i < formData.phones.length; i++) {
            const phone = formData.phones[i];
            if (phone?.phone && !validatePhoneNumber(phone.phone)) {
                setErrorMessage(t('pages.bookingSystem.NewContact.phoneMustBeValidAtLeast', { v1: i + 1, phone: phone.phone }));
                setShowToast(true);
                setIsSubmitting(false);
                return; // Stop here if any phone is invalid
            }
        }
        // Validate required fields
        try {
            const contactFormData: ContactFormData = {
                ...formData,
                avatar,
                customFields: formData.customFields // ✅ This has your reordered fields
            };

            if (isEditMode && id) {
                // Update existing contact
                await contactService.contacts.updateContact({
                    ...contactFormData,
                    id,
                    customFields: formData.customFields // ✅ Use YOUR order
                });

                // displayNameSource is app-controlled only — Wix has no equivalent field,
                // so it's saved as a separate direct Firestore write, not via updateContact.
                await contactService.contacts.updateDisplayNameSource(id, formData.displayNameSource || '');

                // Same as the contact page's own saves: hold the saved values on the contact page over the older
                // Firestore snapshots that arrive before the Wix webhook, and say what was updated.
                const original = originalContactRef.current;
                if (original) {
                    const saved = { ...original, ...contactFormData, id } as unknown as Contact;
                    pinContactSaved(id, saved, original);
                    present(contactSavedToast(original, saved, t));
                } else {
                    present(contactSavedToast({} as Contact, {} as Contact, t));
                }

                // Update cache with edited contact data
                const cached = localStorage.getItem(`contact-${id}`);
                if (cached) {
                    const cachedData = JSON.parse(cached);
                    const updatedContact = { ...cachedData, ...contactFormData, customFields: formData.customFields };
                    localStorage.setItem(`contact-${id}`, JSON.stringify(updatedContact));
                }

                // Mark that the contact list needs to be refreshed to show updated contact
                localStorage.setItem('contactListNeedsRefresh', 'true');
                
                // ✅ NEW: Check if onClose callback exists (modal context)
                if (onClose) {
                    onClose();
                    return;
                }
                
                window.history.back();
                return;
            } else {
                // Create new contact logic remains unchanged
                const newContact = await contactService.contacts.createContact(contactFormData);

                if (newContact && newContact.id) {
                    // Same as edit mode — direct Firestore write, independent of the Wix-
                    // oriented createContact() call above.
                    if (formData.displayNameSource) {
                        await contactService.contacts.updateDisplayNameSource(newContact.id, formData.displayNameSource);
                    }

                    // Mark that the contact list needs to be refreshed
                    localStorage.setItem('contactListNeedsRefresh', 'true');
                    
                    // ✅ NEW: Check if onClose callback exists (modal context)
                    if (onClose) {
                        onClose();
                        return;
                    }
                    
                    ionRouter.push(`/tabs/contact/${newContact.id}`, 'forward', 'replace');
                    return;
                }
            }

            // Fallback navigation
            // ✅ NEW: Check if onClose callback exists before fallback navigation
            if (onClose) {
                onClose();
                return;
            }
            
            ionRouter.push('/tabs/contacts', 'back', 'pop');
        } catch {
            setErrorMessage((isEditMode ? t('pages.bookingSystem.NewContact.failedToUpdateContactPleaseTryAgain') : t('pages.bookingSystem.NewContact.failedToCreateContactPleaseTryAgain')));
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Around line 340, update the handleMakePrimaryEmail function:

    const handleMakePrimaryEmail = (emailIndex: number) => {
        // 🔥 CHECK: Prevent member primary email changes
        if (contactMemberInfo.isMember && emailIndex !== 0) {
            present({
                message: t('pages.bookingSystem.NewContact.cannotChangePrimaryEmailForSite'),
                duration: 6000,
                color: 'danger',
                position: 'top',
                buttons: [
                    {
                        text: t('pages.bookingSystem.NewContact.dismiss'),
                        role: 'cancel',
                    },
                ],
            });
            return; // Exit early - don't make the change
        }

        // Normal logic for non-members
        const newEmails = [...formData.emails];
        // Set all to non-primary
        newEmails.forEach(email => email.primary = false);
        // Set selected email as primary
        newEmails[emailIndex].primary = true;

        // Move primary email to first position
        const primaryEmail = newEmails[emailIndex];
        newEmails.splice(emailIndex, 1);
        newEmails.unshift(primaryEmail);

        setFormData({ ...formData, emails: newEmails });
    };

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const fromPath = params.get('from') || "/tabs/contacts";

    // Update the handle type change functions
    const handleEmailTypeChange = (type: string) => {
        setEmailType(type);

        setFormData(prev => {
            const newEmails = [...prev.emails];

            if (newEmails.length === 0) {
                newEmails.push({
                    id: generateUniqueId(),
                    email: '',
                    tag: type,  // ✅ Use 'type' parameter
                    primary: true
                });
            } else {
                newEmails[0] = {
                    ...newEmails[0],
                    tag: type  // ✅ Use 'type' parameter, not customValue
                };
            }

            return {
                ...prev,
                emails: newEmails
            };
        });
    };

    // Around line 400, fix the handlePhoneTypeChange function:

    const handlePhoneTypeChange = (newType: string) => {
        // Update the phoneType state first
        setPhoneType(newType);

        // Update the PRIMARY phone's tag (index 0)
        setFormData(prev => {
            const updatedPhones = [...(prev.phones || [])];

            if (updatedPhones.length > 0) {
                // Update the first phone's tag (primary phone)
                updatedPhones[0] = {
                    ...updatedPhones[0],
                    tag: newType
                };
            }

            return {
                ...prev,
                phones: updatedPhones
            };
        });
    };

    // Write a processed phone number into the phone at `id` (or create the
    // primary entry if the list is empty). Shared by the primary + additional
    // InlineTypePicker inputs.
    const setPhoneValue = (id: string | null, raw: string) => {
        const processed = processPhoneWithLibrary(raw);
        setFormData(prev => {
            const phones = [...prev.phones];
            const idx = id ? phones.findIndex(p => p.id === id) : 0;
            if (phones.length === 0) {
                phones.push({
                    id: generateUniqueId(),
                    phone: processed.phone,
                    tag: phoneType,
                    primary: true,
                    formattedPhone: processed.formattedPhone,
                    e164Phone: processed.e164Phone,
                    countryCode: processed.countryCode,
                });
            } else if (idx !== -1) {
                phones[idx] = {
                    ...phones[idx],
                    phone: processed.phone,
                    formattedPhone: processed.formattedPhone,
                    e164Phone: processed.e164Phone,
                    countryCode: processed.countryCode,
                };
            }
            return { ...prev, phones };
        });
        if (validationErrors.phone) {
            setValidationErrors(prev => ({ ...prev, phone: false }));
        }
    };

    const setPhoneTag = (id: string, tag: string) => {
        setFormData(prev => {
            const phones = [...prev.phones];
            const idx = phones.findIndex(p => p.id === id);
            if (idx !== -1) phones[idx] = { ...phones[idx], tag };
            return { ...prev, phones };
        });
    };

    const setEmailValue = (id: string | null, email: string) => {
        setFormData(prev => {
            const emails = [...prev.emails];
            const idx = id ? emails.findIndex(e => e.id === id) : 0;
            if (emails.length === 0) {
                emails.push({ id: generateUniqueId(), email, tag: emailType, primary: true });
            } else if (idx !== -1) {
                emails[idx] = { ...emails[idx], email };
            }
            return { ...prev, emails };
        });
        if (validationErrors.email) {
            setValidationErrors(prev => ({ ...prev, email: false }));
        }
    };

    const setEmailTag = (id: string, tag: string) => {
        setFormData(prev => {
            const emails = [...prev.emails];
            const idx = emails.findIndex(e => e.id === id);
            if (idx !== -1) emails[idx] = { ...emails[idx], tag };
            return { ...prev, emails };
        });
    };

    const setAddressLine1 = (id: string | null, addressLine1: string) => {
        setFormData(prev => {
            const addresses = [...prev.addresses];
            const idx = id ? addresses.findIndex(a => a.id === id) : 0;
            if (addresses.length === 0) {
                addresses.push({
                    id: generateUniqueId(),
                    address: { addressLine1, addressLine2: '', city: '', subdivision: '', postalCode: '', country: '' },
                    tag: addressType,
                    primary: true,
                });
            } else if (idx !== -1) {
                addresses[idx] = { ...addresses[idx], address: { ...addresses[idx].address, addressLine1 } };
            }
            return { ...prev, addresses };
        });
        if (validationErrors.addressLine1) {
            setValidationErrors(prev => ({ ...prev, addressLine1: false }));
        }
    };

    const setAddressTag = (id: string, tag: string) => {
        setFormData(prev => {
            const addresses = [...prev.addresses];
            const idx = addresses.findIndex(a => a.id === id);
            if (idx !== -1) addresses[idx] = { ...addresses[idx], tag };
            return { ...prev, addresses };
        });
    };

    const setAddressField = (
        id: string | null,
        key: 'subdivision' | 'country',
        val: string,
    ) => {
        setFormData(prev => {
            const addresses = [...prev.addresses];
            const idx = id ? addresses.findIndex(a => a.id === id) : 0;
            const blank = { addressLine1: '', addressLine2: '', city: '', subdivision: '', postalCode: '', country: '' };
            if (addresses.length === 0) {
                addresses.push({ id: generateUniqueId(), address: { ...blank, [key]: val }, tag: addressType, primary: true });
            } else if (idx !== -1) {
                addresses[idx] = { ...addresses[idx], address: { ...addresses[idx].address, [key]: val } };
            }
            return { ...prev, addresses };
        });
        if (key === 'subdivision' && validationErrors.state) {
            setValidationErrors(prev => ({ ...prev, state: false }));
        }
        if (key === 'country' && validationErrors.country) {
            setValidationErrors(prev => ({ ...prev, country: false }));
        }
    };

    const handleAddressTypeChange = (type: string) => {
        setAddressType(type);

        setFormData(prev => {
            const newAddresses = [...prev.addresses];

            if (newAddresses.length === 0) {
                newAddresses.push({
                    id: generateUniqueId(),
                    address: {
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        subdivision: '',
                        postalCode: '',
                        country: ''
                    },
                    tag: type,  // ✅ Use 'type' parameter
                    primary: true
                });
            } else {
                newAddresses[0] = {
                    ...newAddresses[0],
                    tag: type  // ✅ Use 'type' parameter, not customValue
                };
            }

            return {
                ...prev,
                addresses: newAddresses
            };
        });
    };

    const handleSetPrimaryEmail = (emailId: string | null) => {
        if (!emailId) return;

        const emailIndex = formData.emails.findIndex(e => e.id === emailId);
        if (emailIndex !== -1) {
            handleMakePrimaryEmail(emailIndex);
        }
    };

    // Around line 484, replace the existing handleSetPrimaryPhone function:

    const handleSetPrimaryPhone = (phoneId: string | null) => {
        if (!phoneId) return;

        setFormData(prev => {
            const phoneToPromote = prev.phones.find(p => p.id === phoneId);
            if (!phoneToPromote) return prev;

            // Make all phones non-primary first
            const updatedPhones = prev.phones.map(phone => ({
                ...phone,
                primary: false
            }));

            // Find and set the selected phone as primary
            const phoneIndex = updatedPhones.findIndex(p => p.id === phoneId);
            if (phoneIndex !== -1) {
                updatedPhones[phoneIndex].primary = true;

                // Move primary phone to first position
                const primaryPhone = updatedPhones[phoneIndex];
                const otherPhones = updatedPhones.filter(p => p.id !== phoneId);

                // Update phoneType state to match the new primary phone's tag
                setPhoneType(primaryPhone.tag || 'Mobile');

                return {
                    ...prev,
                    phones: [primaryPhone, ...otherPhones]
                };
            }

            return { ...prev, phones: updatedPhones };
        });
    };

    const handleSetPrimaryAddress = (addressId: string | null) => {
        if (!addressId) return;

        setFormData(prev => {
            const addressToPromote = prev.addresses.find(a => a.id === addressId);
            if (!addressToPromote) return prev;

            // Make all addresses non-primary first
            const updatedAddresses = prev.addresses.map(address => ({
                ...address,
                primary: false
            }));

            // Find and set the selected address as primary
            const addressIndex = updatedAddresses.findIndex(a => a.id === addressId);
            if (addressIndex !== -1) {
                updatedAddresses[addressIndex].primary = true;

                // Move primary address to first position
                const primaryAddress = updatedAddresses[addressIndex];
                const otherAddresses = updatedAddresses.filter(a => a.id !== addressId);

                // Update addressType state to match the new primary address's tag
                setAddressType(primaryAddress.tag || 'Home');

                return {
                    ...prev,
                    addresses: [primaryAddress, ...otherAddresses]
                };
            }

            return { ...prev, addresses: updatedAddresses };
        });
    };

    // Add this effect to debug when formData changes
    useEffect(() => {
        // Debug effect for phone type state
    }, [phoneType, formData.phones]);

    // Change to save global order (admin only)
    const handleCustomFieldReorder = async (event: CustomEvent<ItemReorderEventDetail>) => {
 
        // This is the correct Ionic pattern: call complete() to get the reordered array and update the DOM.
        const reorderedFields = event.detail.complete(formData.customFields);
 
        if (!reorderedFields) {
            console.error('Reorder operation failed.');
            return;
        }
 
        // Update the component's state to match the new order for a responsive UI.
        setFormData(prev => ({
            ...prev,
            customFields: reorderedFields
        }));
 
        // Save the new global order to the backend.
        const fieldOrder = reorderedFields
            .map((f: CustomField) => f?.fieldKey || f?.label)
            .filter(Boolean) as string[];
 
        if (fieldOrder.length > 0) {
            const success = await contactService.saveGlobalFieldOrder(fieldOrder);
            if (!success) {
                present({ message: t('pages.bookingSystem.NewContact.failedToSaveFieldOrderChanges'), duration: 3000, color: 'danger' });
            }
        }
    };

    return (
        <div className={styles.modalWrapper}>
        <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
        <BasePageWithHeader
            title={isEditMode ? t('pages.bookingSystem.NewContact.editContact') : t('pages.bookingSystem.NewContact.newContact')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
                onClose ? (
                    <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
                        <IonIcon icon={closeOutline} />
                    </IonButton>
                ) : (
                    <IonButton
                        shape="round"
                        onClick={() => ionRouter.push(fromPath, 'back', 'pop')}
                        className={styles.backButton}
                    >
                        <IonIcon icon={chevronBack} />
                    </IonButton>
                )
            }
            endButtons={
                <IonButton fill="clear" onClick={handleSubmit} className={styles.saveButton} disabled={isSubmitting || !isFormValid}>
                    {isSubmitting ? t('pages.bookingSystem.NewContact.saving') : t('pages.bookingSystem.NewContact.save')}
                </IonButton>
            }
        >

            <IonContent scrollY={false} className={styles.content}>
                <IonGrid className={styles.formContainer}>
                <IonList className={styles.formList}>
                    <IonItem lines="none" className={styles.avatarContainer}>
                        <IonAvatar>
                            <img src={avatar} alt={t('pages.bookingSystem.NewContact.contactAvatar')} loading="eager" />
                        </IonAvatar>
                    </IonItem>

                    <div className={styles.sectionGroup}>
                    {/* ✅ NAME SECTION */}
                    <IonItem className={validationErrors.firstName ? styles.errorItem : ''}>
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.firstName')}</IonLabel>
                        <IonInput
                            id="firstName-input"
                            value={formData.firstName}
                            onIonInput={e => {
                                setFormData({ ...formData, firstName: e.detail.value! });
                                if (validationErrors.firstName) {
                                    setValidationErrors(prev => ({ ...prev, firstName: false }));
                                }
                            }}
                            placeholder={t('pages.bookingSystem.NewContact.enterFirstName')}
                            required
                        />
                    </IonItem>

                    <IonItem className={validationErrors.lastName ? styles.errorItem : ''}>
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.lastName')}</IonLabel>
                        <IonInput

                            id="lastName-input"
                            value={formData.lastName}
                            onIonInput={e => {
                                setFormData({ ...formData, lastName: e.detail.value! });
                                if (validationErrors.lastName) {
                                    setValidationErrors(prev => ({ ...prev, lastName: false }));
                                }
                            }}
                            placeholder={t('pages.bookingSystem.NewContact.enterLastName')}
                            required
                        />
                    </IonItem>

                    {/* Display Name — a choice of which existing field feeds Zoho's Display
                        Name (Company or Full Name), not free text. Saved directly to
                        Firestore (bypasses Wix, which has no equivalent field). Never
                        defaulted from name/company by the backend sync; if left unset, it
                        falls back to the full name once, only when the Zoho contact is
                        first created. */}
                    <IonItem className={styles.displayNameSourceItem} lines="none">
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.displayName')}</IonLabel>
                        <IonButton
                            fill="clear"
                            ref={displayNameSourceFieldRef}
                            className={styles.displayNameSourceTrigger}
                            onClick={() => setIsDisplayNameSourceOpen(prev => !prev)}
                        >
                            <IonText
                                className={styles.displayNameSourceText}
                                style={{
                                    color: formData.displayNameSource
                                        ? 'var(--glass-card-text, var(--ion-text-color))'
                                        : 'var(--glass-card-text-secondary, var(--ion-color-medium))',
                                    opacity: formData.displayNameSource ? 1 : 0.75,
                                }}
                            >
                                {DISPLAY_NAME_SOURCE_OPTIONS.find(opt => opt.value === formData.displayNameSource)?.label || t('pages.bookingSystem.NewContact.selectDisplayName')}
                            </IonText>
                            <IonIcon icon={chevronDownOutline} className={styles.displayNameSourceCaret} />
                        </IonButton>
                    </IonItem>

                    <PickerDropdown
                        isOpen={isDisplayNameSourceOpen}
                        fieldRef={displayNameSourceFieldRef as React.RefObject<HTMLElement | null>}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        close={() => setIsDisplayNameSourceOpen(false)}
                        align="left"
                        matchWidth
                        className={styles.displayNameSourceDropdown}
                    >
                        <IonList lines="none" className={styles.displayNameSourceDropdownList}>
                            {DISPLAY_NAME_SOURCE_OPTIONS.map(opt => (
                                <IonItem
                                    key={opt.value}
                                    button
                                    lines="none"
                                    detail={false}
                                    className={`${styles.displayNameSourceDropdownItem}${formData.displayNameSource === opt.value ? ` ${styles.displayNameSourceDropdownItemActive}` : ''}`}
                                    onClick={() => {
                                        setFormData({ ...formData, displayNameSource: opt.value });
                                        setIsDisplayNameSourceOpen(false);
                                    }}
                                >
                                    <IonLabel>{opt.label}</IonLabel>
                                </IonItem>
                            ))}
                        </IonList>
                    </PickerDropdown>

                    {/* Optional standard fields — Company / Position / Birthdate. Already
                        supported end-to-end via createContact/updateContact; only added
                        via the "Add New Field" sheet further down the form. */}
                    {addedOptionalFields.includes('company') && (
                        <IonRow className={styles.optionalFieldRow}>
                            <IonCol size="12" className={styles.optionalFieldLabel}>
                                <IonText className={styles.optionalFieldLabelText}>{t('pages.bookingSystem.NewContact.company')}</IonText>
                                <IonIcon
                                    icon={trashOutline}
                                    className={styles.optionalFieldDelete}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, company: '' }));
                                        setAddedOptionalFields(prev => prev.filter(f => f !== 'company'));
                                    }}
                                />
                            </IonCol>
                            <IonCol size="12">
                                <IonInput
                                    className={styles.optionalFieldInput}
                                    id="company-input"
                                    value={formData.company || ''}
                                    onIonInput={e => setFormData({ ...formData, company: e.detail.value! })}
                                    placeholder={t('pages.bookingSystem.NewContact.enterCompanyName')}
                                />
                            </IonCol>
                        </IonRow>
                    )}
                    {addedOptionalFields.includes('position') && (
                        <IonRow className={styles.optionalFieldRow}>
                            <IonCol size="12" className={styles.optionalFieldLabel}>
                                <IonText className={styles.optionalFieldLabelText}>{t('pages.bookingSystem.NewContact.position')}</IonText>
                                <IonIcon
                                    icon={trashOutline}
                                    className={styles.optionalFieldDelete}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, jobTitle: '' }));
                                        setAddedOptionalFields(prev => prev.filter(f => f !== 'position'));
                                    }}
                                />
                            </IonCol>
                            <IonCol size="12">
                                <IonInput
                                    className={styles.optionalFieldInput}
                                    id="position-input"
                                    value={formData.jobTitle || ''}
                                    onIonInput={e => setFormData({ ...formData, jobTitle: e.detail.value! })}
                                    placeholder={t('pages.bookingSystem.NewContact.enterJobTitle')}
                                />
                            </IonCol>
                        </IonRow>
                    )}
                    {addedOptionalFields.includes('birthdate') && (
                        <IonRow className={styles.optionalFieldRow}>
                            <IonCol size="12" className={styles.optionalFieldLabel}>
                                <IonText className={styles.optionalFieldLabelText}>{t('pages.bookingSystem.NewContact.birthdate')}</IonText>
                                <IonIcon
                                    icon={trashOutline}
                                    className={styles.optionalFieldDelete}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, birthdate: '' }));
                                        setAddedOptionalFields(prev => prev.filter(f => f !== 'birthdate'));
                                    }}
                                />
                            </IonCol>
                            <IonCol size="12">
                                <IonButton
                                    ref={birthdateFieldRef}
                                    fill="clear"
                                    className={`${styles.birthdateTrigger} ${isBirthdateOpen ? styles.birthdateTriggerOpen : ''}`}
                                    onClick={() => setIsBirthdateOpen(o => !o)}
                                >
                                    <IonText
                                        className={styles.birthdateTriggerText}
                                        style={{
                                            color: formData.birthdate
                                                ? 'var(--glass-card-text, var(--ion-text-color))'
                                                : 'var(--glass-card-text-secondary, var(--ion-color-medium))',
                                        }}
                                    >
                                        {formData.birthdate
                                            ? new Date(String(formData.birthdate) + 'T12:00:00').toLocaleDateString(intlLocale(), { month: 'long', day: 'numeric', year: 'numeric' })
                                            : t('pages.bookingSystem.NewContact.selectBirthdate')}
                                    </IonText>
                                    <IonIcon icon={calendarOutline} className={styles.birthdateTriggerCaret} />
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    )}
                    <PickerDropdown
                        isOpen={isBirthdateOpen}
                        fieldRef={birthdateFieldRef as React.RefObject<HTMLElement | null>}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        close={() => setIsBirthdateOpen(false)}
                        align="left"
                        className={styles.birthdateDropdown}
                    >
                        <MiniCalendarPicker
                            value={formData.birthdate ? new Date(String(formData.birthdate) + 'T12:00:00') : null}
                            onSelect={(d) => {
                                const y = d.getFullYear();
                                const m = String(d.getMonth() + 1).padStart(2, '0');
                                const day = String(d.getDate()).padStart(2, '0');
                                setFormData(prev => ({ ...prev, birthdate: `${y}-${m}-${day}` }));
                                setIsBirthdateOpen(false);
                            }}
                        />
                    </PickerDropdown>
                    </div>

                    <div className={styles.sectionGroup}>
                    {/* ✅ PHONE SECTION DIVIDER */}
                    <IonText className={styles.sectionDivider}>
                        <h3>{t('pages.bookingSystem.NewContact.phoneInformation')}</h3>
                    </IonText>

                    {/* Primary Phone */}
                    <PhoneAgentInput
                        label={t('pages.bookingSystem.NewContact.phone')}
                        boxClassName={styles.phoneFieldRow}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        required
                        hasError={validationErrors.phone}
                        value={formData.phones[0]?.e164Phone || formData.phones[0]?.phone || ''}
                        onChange={(v) => setPhoneValue(formData.phones[0]?.id ?? null, v)}
                        typeOptions={PHONE_TYPE_OPTIONS}
                        currentType={formData.phones[0]?.tag || phoneType}
                        onTypeChange={handlePhoneTypeChange}
                    />

                    {/* Additional Phones */}
                    {formData.phones.slice(1).map((phoneField) => (
                        <PhoneAgentInput
                            key={phoneField.id}
                            label={t('pages.bookingSystem.NewContact.additionalPhone')}
                            boxClassName={styles.phoneFieldRow}
                            portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                            value={
                                formData.phones.find(p => p.id === phoneField.id)?.e164Phone ||
                                formData.phones.find(p => p.id === phoneField.id)?.phone ||
                                ''
                            }
                            onChange={(v) => setPhoneValue(phoneField.id, v)}
                            typeOptions={PHONE_TYPE_OPTIONS}
                            currentType={formData.phones.find(p => p.id === phoneField.id)?.tag || 'mobile'}
                            onTypeChange={(type) => setPhoneTag(phoneField.id, type)}
                            isPrimary={formData.phones.find(p => p.id === phoneField.id)?.primary}
                            onMakePrimary={() => handleSetPrimaryPhone(phoneField.id)}
                            onDelete={() => removeField('phones', phoneField.id)}
                        />
                    ))}

                    {/* Add Phone Button */}
                    <IonItem lines="none" className={styles.inlineActionRow}>
                        <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => addField('phones')}
                            className={styles.addButton}
                        >
                            <IonIcon slot="start" icon={add} className={styles.addIcon} />
                            <IonText className={styles.buttonText}>{t('pages.bookingSystem.NewContact.addPhone')}</IonText>
                        </IonButton>
                    </IonItem>
                    </div>

                    <div className={styles.sectionGroup}>
                    {/* ✅ EMAIL SECTION DIVIDER */}
                    <IonText className={styles.sectionDivider}>
                        <h3>{t('pages.bookingSystem.NewContact.emailInformation')}</h3>
                    </IonText>

                    {/* Primary Email */}
                    <InlineTypePicker
                        mode="email"
                        label={t('pages.bookingSystem.NewContact.email')}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        inputId="email-input"
                        inputType="email"
                        placeholder={t('pages.bookingSystem.NewContact.enterEmail')}
                        required
                        hasError={validationErrors.email}
                        value={formData.emails[0]?.email || ''}
                        onValueChange={(v) => setEmailValue(formData.emails[0]?.id ?? null, v)}
                        currentType={formData.emails[0]?.tag || emailType}
                        onTypeChange={handleEmailTypeChange}
                    />

                    {/* Additional Emails */}
                    {formData.emails.slice(1).map((emailField) => (
                        <InlineTypePicker
                            key={emailField.id}
                            mode="email"
                            label={t('pages.bookingSystem.NewContact.additionalEmail')}
                            portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                            inputId={`email-${emailField.id}`}
                            inputType="email"
                            placeholder={t('pages.bookingSystem.NewContact.enterEmail')}
                            value={formData.emails.find(e => e.id === emailField.id)?.email || ''}
                            onValueChange={(v) => setEmailValue(emailField.id, v)}
                            currentType={formData.emails.find(e => e.id === emailField.id)?.tag || 'personal'}
                            onTypeChange={(type) => setEmailTag(emailField.id, type)}
                            isPrimary={formData.emails.find(e => e.id === emailField.id)?.primary}
                            onMakePrimary={() => handleSetPrimaryEmail(emailField.id)}
                            onDelete={() => removeField('emails', emailField.id)}
                        />
                    ))}

                    {/* Add Email Button */}
                    <IonItem lines="none" className={styles.inlineActionRow}>
                        <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => addField('emails')}
                            className={styles.addButton}
                        >
                            <IonIcon slot="start" icon={add} className={styles.addIcon} />
                            <IonText className={styles.buttonText}>{t('pages.bookingSystem.NewContact.addEmail')}</IonText>
                        </IonButton>
                    </IonItem>
                    </div>

                    <div className={styles.sectionGroup}>
                    {/* ✅ ADDRESS SECTION DIVIDER */}
                    <IonText className={styles.sectionDivider}>
                        <h3>{t('pages.bookingSystem.NewContact.addressInformation')}</h3>
                    </IonText>

                    {/* Primary Address Fields */}
                    <InlineTypePicker
                        mode="address"
                        label={t('pages.bookingSystem.NewContact.streetAddress')}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        inputId="addressLine1-input"
                        placeholder={t('pages.bookingSystem.NewContact.streetAddress2')}
                        required
                        hasError={validationErrors.addressLine1}
                        value={formData.addresses[0]?.address.addressLine1 || ''}
                        onValueChange={(v) => setAddressLine1(formData.addresses[0]?.id ?? null, v)}
                        currentType={formData.addresses[0]?.tag || addressType}
                        onTypeChange={handleAddressTypeChange}
                    />
                    <IonItem>
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.aptSuiteUnit')}</IonLabel>
                        <IonInput
                            id="addressLine2-input"
                            value={formData.addresses[0]?.address.addressLine2 || ''}
                            onIonInput={e => {
                                const newAddresses = [...formData.addresses];
                                if (newAddresses.length === 0) {
                                    newAddresses.push({
                                        id: generateUniqueId(),
                                        address: {
                                            addressLine1: '',
                                            addressLine2: e.detail.value!,
                                            city: '',
                                            subdivision: '',
                                            postalCode: '',
                                            country: ''
                                        },
                                        tag: 'home',
                                        primary: true
                                    });
                                } else {
                                    newAddresses[0].address.addressLine2 = e.detail.value!;
                                }
                                setFormData({ ...formData, addresses: newAddresses });
                            }}
                            placeholder={t('pages.bookingSystem.NewContact.apartmentSuiteUnitEtcOptional')}

                        />
                    </IonItem>

                    <IonItem className={validationErrors.city ? styles.errorItem : ''}>
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.city')}</IonLabel>
                        <IonInput
                            id="city-input"
                            value={formData.addresses[0]?.address.city || ''}
                            onIonInput={e => {
                                const newAddresses = [...formData.addresses];
                                if (newAddresses.length === 0) {
                                    newAddresses.push({
                                        id: generateUniqueId(),
                                        address: {
                                            addressLine1: '',
                                            addressLine2: '',
                                            city: e.detail.value!,
                                            subdivision: '',
                                            postalCode: '',
                                            country: ''
                                        },
                                        tag: 'home',
                                        primary: true
                                    });
                                } else {
                                    newAddresses[0].address.city = e.detail.value!;
                                }
                                setFormData({ ...formData, addresses: newAddresses }); // ✅ Use functional update
                                if (validationErrors.city) {
                                    setValidationErrors(prev => ({ ...prev, city: false }));
                                }
                            }}
                            placeholder={t('pages.bookingSystem.NewContact.enterCity')}
                            required
                        />
                    </IonItem>

                    <IonItem className={validationErrors.zipCode ? styles.errorItem : ''}>
                        <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.zipPostalCode')}</IonLabel>
                        <IonInput
                            id="zipCode-input"
                            value={formData.addresses[0]?.address.postalCode || ''}
                            onIonInput={e => {
                                const newAddresses = [...formData.addresses];
                                if (newAddresses.length === 0) {
                                    newAddresses.push({
                                        id: generateUniqueId(),
                                        address: {
                                            addressLine1: '',
                                            addressLine2: '',
                                            city: '',
                                            subdivision: '',
                                            postalCode: e.detail.value!,
                                            country: ''
                                        },
                                        tag: 'home',
                                        primary: true
                                    });
                                } else {
                                    newAddresses[0].address.postalCode = e.detail.value!;
                                }
                                setFormData({ ...formData, addresses: newAddresses }); // ✅ Use functional update
                                if (validationErrors.zipCode) {
                                    setValidationErrors(prev => ({ ...prev, zipCode: false }));
                                }
                            }}
                            placeholder={t('pages.bookingSystem.NewContact.enterZipPostalCode')}
                            required
                        />
                    </IonItem>

                    <LocationPicker
                        label={t('pages.bookingSystem.NewContact.stateProvince')}
                        type="state"
                        placeholder={t('pages.bookingSystem.NewContact.selectState')}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        inputId={STATE_TRIGGER_ID}
                        value={formData.addresses[0]?.address.subdivision || ''}
                        onChange={(v) => setAddressField(formData.addresses[0]?.id ?? null, 'subdivision', v)}
                    />

                    <LocationPicker
                        label={t('pages.bookingSystem.NewContact.country')}
                        type="country"
                        placeholder={t('pages.bookingSystem.NewContact.selectCountry')}
                        portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                        inputId={COUNTRY_TRIGGER_ID}
                        value={formData.addresses[0]?.address.country || ''}
                        onChange={(v) => setAddressField(formData.addresses[0]?.id ?? null, 'country', v)}
                    />

                    {/* Additional Addresses */}
                    {formData.addresses.slice(1).map((addressField, _index) => (
                        <React.Fragment key={addressField.id}>
                            {/* ✅ Add divider before each additional address */}
                            <IonText className={styles.additionalAddressDivider}>
                                <h3>{t('pages.bookingSystem.NewContact.additionalAddress')} {_index + 1}</h3>
                            </IonText>
                            <InlineTypePicker
                                mode="address"
                                label={t('pages.bookingSystem.NewContact.additionalAddress')}
                                portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                                inputId={`additional-address-street-${addressField.id}`}
                                placeholder={t('pages.bookingSystem.NewContact.streetAddress2')}
                                value={formData.addresses.find(a => a.id === addressField.id)?.address.addressLine1 || ''}
                                onValueChange={(v) => setAddressLine1(addressField.id, v)}
                                currentType={formData.addresses.find(a => a.id === addressField.id)?.tag || 'home'}
                                onTypeChange={(type) => setAddressTag(addressField.id, type)}
                                isPrimary={formData.addresses.find(a => a.id === addressField.id)?.primary}
                                onMakePrimary={() => handleSetPrimaryAddress(addressField.id)}
                                onDelete={() => removeField('addresses', addressField.id)}
                            />

                            <IonItem>
                                <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.aptSuiteUnit')}</IonLabel>
                                <IonInput
                                    id={`additional-address-street2-${addressField.id}`}
                                    value={formData.addresses.find(a => a.id === addressField.id)?.address.addressLine2 || ''}
                                    onIonInput={e => {
                                        const newAddresses = [...formData.addresses];
                                        const addressIndex = newAddresses.findIndex(a => a.id === addressField.id);
                                        if (addressIndex !== -1) {
                                            newAddresses[addressIndex].address.addressLine2 = e.detail.value!;
                                            setFormData({ ...formData, addresses: newAddresses });
                                        }
                                    }}
                                    placeholder={t('pages.bookingSystem.NewContact.apartmentSuiteUnitEtcOptional')}
                                />
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.city2')}</IonLabel>
                                <IonInput
                                    id={`additional-address-city-${addressField.id}`}
                                    value={formData.addresses.find(a => a.id === addressField.id)?.address.city || ''}
                                    onIonInput={e => {
                                        const newAddresses = [...formData.addresses];
                                        const addressIndex = newAddresses.findIndex(a => a.id === addressField.id);
                                        if (addressIndex !== -1) {
                                            newAddresses[addressIndex].address.city = e.detail.value!;
                                            setFormData({ ...formData, addresses: newAddresses });
                                        }
                                    }}
                                    placeholder={t('pages.bookingSystem.NewContact.enterCity')}
                                />
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">{t('pages.bookingSystem.NewContact.zipPostalCode2')}</IonLabel>
                                <IonInput
                                    id={`additional-address-zip-${addressField.id}`}
                                    value={formData.addresses.find(a => a.id === addressField.id)?.address.postalCode || ''}
                                    onIonInput={e => {
                                        const newAddresses = [...formData.addresses];
                                        const addressIndex = newAddresses.findIndex(a => a.id === addressField.id);
                                        if (addressIndex !== -1) {
                                            newAddresses[addressIndex].address.postalCode = e.detail.value!;
                                            setFormData({ ...formData, addresses: newAddresses });
                                        }
                                    }}
                                    placeholder={t('pages.bookingSystem.NewContact.enterZipPostalCode')}
                                />
                            </IonItem>

                            <LocationPicker
                                label={t('pages.bookingSystem.NewContact.stateProvince2')}
                                type="state"
                                placeholder={t('pages.bookingSystem.NewContact.selectState')}
                                portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                                inputId={`additional-state-${addressField.id}`}
                                value={formData.addresses.find(a => a.id === addressField.id)?.address.subdivision || ''}
                                onChange={(v) => setAddressField(addressField.id, 'subdivision', v)}
                            />
                            <LocationPicker
                                label={t('pages.bookingSystem.NewContact.country2')}
                                type="country"
                                placeholder={t('pages.bookingSystem.NewContact.selectCountry')}
                                portalRef={pickerPortalRef as React.RefObject<HTMLElement | null>}
                                inputId={`additional-country-${addressField.id}`}
                                value={formData.addresses.find(a => a.id === addressField.id)?.address.country || ''}
                                onChange={(v) => setAddressField(addressField.id, 'country', v)}
                            />

                        </React.Fragment>
                    ))}

                    {/* Add Address Button */}
                    <IonItem lines="none" className={styles.inlineActionRow}>
                        <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => addField('addresses')}
                            className={styles.addButton}
                        >
                            <IonIcon slot="start" icon={add} className={styles.addIcon} />
                            <IonText className={styles.buttonText}>{t('pages.bookingSystem.NewContact.addAddress')}</IonText>
                        </IonButton>
                    </IonItem>
                    </div>

                    <div className={styles.sectionGroup}>
                    {/* ✅ CUSTOM FIELDS SECTION DIVIDER (only show if there are custom fields) */}
                    {(formData.customFields.length > 0 || modalCustomFields.length > 0) && (
                        <IonText className={styles.sectionDivider}>
                            <h3>{t('pages.bookingSystem.NewContact.additionalInformation')}</h3>
                        </IonText>
                    )}

                    {/* Custom Fields Section - FIX THE REORDER STRUCTURE */}
                    {formData.customFields && formData.customFields.length > 0 && (
                        <IonReorderGroup disabled={false} onIonItemReorder={handleCustomFieldReorder}>
                            {formData.customFields.map((field, index) => (
                                <IonItem key={`${field.fieldKey}-${index}`}>
                                    <IonLabel position="stacked">{field.label}</IonLabel>

                                    {/* Your existing input logic */}
                                    {field.type === 'number' ? (
                                        <IonInput
                                            id={`custom-field-${field.id}`}
                                            type="number"
                                            value={field.value}
                                            onIonInput={e => {
                                                const newFields = [...formData.customFields];
                                                newFields[index].value = e.detail.value!;
                                                setFormData({ ...formData, customFields: newFields });
                                            }}
                                            placeholder={t('pages.bookingSystem.NewContact.enter', { v1: field.label.toLowerCase() })}
                                        />
                                    ) : field.type === 'date' ? (
                                        <IonInput
                                            id={`custom-field-${field.id}`}
                                            type="date"
                                            value={field.value}
                                            onIonInput={e => {
                                                const newFields = [...formData.customFields];
                                                newFields[index].value = e.detail.value!;
                                                setFormData({ ...formData, customFields: newFields });
                                            }}
                                            placeholder={t('pages.bookingSystem.NewContact.enter', { v1: field.label.toLowerCase() })}
                                        />
                                    ) : field.type === 'url' ? (
                                        <IonInput
                                            id={`custom-field-${field.id}`}
                                            type="url"
                                            value={field.value}
                                            onIonInput={e => {
                                                const newFields = [...formData.customFields];
                                                newFields[index].value = e.detail.value!;
                                                setFormData({ ...formData, customFields: newFields });
                                            }}
                                            placeholder={t('pages.bookingSystem.NewContact.enter', { v1: field.label.toLowerCase() })}
                                        />
                                    ) : (
                                        <IonInput
                                            id={`custom-field-${field.id}`}
                                            type="text"
                                            value={field.value}
                                            onIonInput={e => {
                                                const newFields = [...formData.customFields];
                                                newFields[index].value = e.detail.value!;
                                                setFormData({ ...formData, customFields: newFields });
                                            }}
                                            placeholder={t('pages.bookingSystem.NewContact.enter', { v1: field.label.toLowerCase() })}
                                        />
                                    )}

                                    <IonButton
                                        fill="clear"
                                        slot="end"
                                        onClick={() => {
                                            const newFields = formData.customFields.filter((_, i) => i !== index);
                                            setFormData(prev => ({
                                                ...prev,
                                                customFields: newFields
                                            }));
                                        }}
                                    >
                                        <IonIcon icon={trash} slot="icon-only" color="danger" />
                                    </IonButton>

                                    {/* ✅ PUT REORDER HANDLE INSIDE THE ITEM, NOT WRAPPING IT */}
                                    <IonReorder slot="end" />
                                </IonItem>
                            ))}
                        </IonReorderGroup>
                    )}


                    {/* Add New Field — one consolidated SheetModal (Email/Phone/Address are
                        redundant with their own +Add buttons above; Company/Position/
                        Birthdate reveal inline near Display Name; Custom Field hands off to
                        CustomFieldsManager's own modal below, opened externally). */}
                    <IonItem lines="none" className={styles.inlineActionRow}>
                        <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => setShowAddFieldSheet(true)}
                            className={styles.addButton}
                        >
                            <IonIcon slot="start" icon={add} className={styles.addIcon} />
                            <IonText className={styles.buttonText}>{t('pages.bookingSystem.NewContact.addNewField')}</IonText>
                        </IonButton>
                    </IonItem>
                    </div>
                    <SheetModal
                        isOpen={showAddFieldSheet}
                        onClose={() => setShowAddFieldSheet(false)}
                        title={t('pages.bookingSystem.NewContact.addNewField')}
                        menuOptions={[
                            { label: t('pages.bookingSystem.NewContact.email2'), icon: mailOutline, onClick: () => { addField('emails'); setShowAddFieldSheet(false); } },
                            { label: t('pages.bookingSystem.NewContact.phone2'), icon: callOutline, onClick: () => { addField('phones'); setShowAddFieldSheet(false); } },
                            { label: t('pages.bookingSystem.NewContact.address'), icon: pinOutline, onClick: () => { addField('addresses'); setShowAddFieldSheet(false); } },
                            ...(!addedOptionalFields.includes('company') ? [{ label: t('pages.bookingSystem.NewContact.company'), icon: businessOutline, onClick: () => { setAddedOptionalFields(prev => [...prev, 'company' as const]); setShowAddFieldSheet(false); } }] : []),
                            ...(!addedOptionalFields.includes('position') ? [{ label: t('pages.bookingSystem.NewContact.position'), icon: briefcaseOutline, onClick: () => { setAddedOptionalFields(prev => [...prev, 'position' as const]); setShowAddFieldSheet(false); } }] : []),
                            ...(!addedOptionalFields.includes('birthdate') ? [{ label: t('pages.bookingSystem.NewContact.birthdate'), icon: calendarOutline, onClick: () => { setAddedOptionalFields(prev => [...prev, 'birthdate' as const]); setShowAddFieldSheet(false); } }] : []),
                            { label: t('pages.bookingSystem.NewContact.customField'), icon: informationCircle, onClick: () => { setShowAddFieldSheet(false); setShowCustomFieldsManager(true); } },
                        ]}
                    />

                    <CustomFieldsManager
                        existingFields={modalCustomFields}
                        onAddField={(newField) => {
                            // ✅ The newField already has fieldKey from CustomFieldsManager
                            // Just add to both modal fields and form fields
                            setModalCustomFields(prev => [...prev, newField]);

                            setFormData(prev => ({
                                ...prev,
                                customFields: [...prev.customFields, newField]
                            }));
                        }}
                        onSelectField={(fieldId) => {
                            // Create new instance from modal fields for form
                            const field = modalCustomFields.find(f => f.id === fieldId);
                            if (field) {
                                const fieldKey = field.fieldKey || `custom.${field.label.toLowerCase().replace(/\s+/g, '-')}`;
                                const newField = {
                                    ...field,
                                    id: generateUniqueId(),
                                    type: field.type || 'personal',
                                    fieldKey: fieldKey // ✅ Always store the actual field key
                                };
                                setFormData(prev => ({
                                    ...prev,
                                    customFields: [...prev.customFields, newField]
                                }));
                            }
                        }}
                        onFieldsChanged={handleRefreshModalFields} // ✅ Add this callback
                        isOpen={showCustomFieldsManager}
                        onDidDismiss={() => setShowCustomFieldsManager(false)}
                        hideTrigger
                    />
                </IonList>

                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={errorMessage}
                    duration={4000}
                    icon={alertCircleOutline}
                    cssClass="app-toast app-toast--error"
                    position="top"
                />






                </IonGrid>
            </IonContent>
        </BasePageWithHeader>
        </div>
    );
};

export default NewContact;
